import animation, { fadeIn } from '@helpers/animation';
import brand from '@helpers/brand';
import { cssVar, setCssVar } from '@helpers/cssVar';
import fonts from '@helpers/fonts';
import { Device, from } from '@helpers/media';
import LinkButtonStyles from '@stories/Components/Buttons/LinkButton/LinkButton.styles';
import HeadingStyles from '@stories/Components/Global/Typography/Heading.styles';
import ParagraphStyles from '@stories/Components/Global/Typography/Paragraph.styles';
import UnorderedListStyles from '@stories/Components/Global/Typography/UnorderedList.styles';
import SectionWrapper from '@stories/Components/SectionWrapper/SectionWrapper';
import styled, { css } from 'styled-components';
const linkStyles = css `
  background-color: transparent;
  background-image: linear-gradient(
    to right,
    ${cssVar('accentColor')} 0%,
    ${cssVar('accentColor')} 100%
  );
  background-repeat: no-repeat;
  background-size: 0% 100%;
  color: ${cssVar('fgColor')};
  ${fonts.sourceSansPro.regular};
  margin: -3px;
  padding: 3px;
  text-decoration: underline;
  text-underline-position: under;
  transition: background-size 0.15s ease-out;

  &:focus {
    outline: none;
  }

  &:focus,
  &:hover {
    background-size: 100% 100%;
  }
`;
const tableStyles = css `
  margin: 0 0 16px;

  th,
  td {
    border: 1px solid ${cssVar('fgColor')};
    color: ${cssVar('fgColor')};
    ${ParagraphStyles.RegularStyles};
    height: auto !important; // override inline height - no point this being content editable.
    margin: 0;
    padding: 8px 16px;
  }
`;
const contentStyles = css `
  color: ${cssVar('fgColor')};
  ${fonts.sourceSansPro.regular};

  h1 {
    ${HeadingStyles.H1Styles};
  }

  h2 {
    ${HeadingStyles.H2Styles};
  }

  h3 {
    ${HeadingStyles.H3Styles};
  }

  h4 {
    ${HeadingStyles.H4Styles};
  }

  h5 {
    ${HeadingStyles.H5Styles};
  }

  h6 {
    ${HeadingStyles.H6Styles};
  }

  p {
    ${ParagraphStyles.RegularStyles};

    .theme-rss & {
      ${ParagraphStyles.SmallStyles};

      @media ${from(Device.Desktop)} {
        font-size: 20px;
      }
    }
  }

  strong {
    ${fonts.sourceSansPro.bold};
  }

  ol,
  ul {
    display: block;
    ${ParagraphStyles.RegularStyles};
    padding: 0 0 0 40px;
  }

  ol {
    list-style: decimal;

    li {
      display: list-item;
    }
  }

  ul {
    padding-left: 0;

    li {
      ${UnorderedListStyles.LiStyles};
      display: list-item;
    }
  }

  a {
    ${linkStyles};
  }

  > *:last-child {
    margin-bottom: 0;
  }

  table {
    ${tableStyles};
  }

  hr {
    margin: 16px 0;
  }

  sup {
    position: relative;
    top: -0.5em;
    font-size: 80%;
  }

  sub {
    position: relative;
    top: 0.5em;
    font-size: 80%;
  }

  blockquote {
    ${fonts.sourceSansPro.semiBoldItalic};
    font-size: 18px;
    line-height: 28px;
    margin: 20px 0;

    @media ${from(Device.TabletLarge)} {
      font-size: 26px;
      line-height: 36px;
    }
  }

  cite {
    ${fonts.sourceSansPro.semiBold};
    font-size: 16px;
    line-height: 26px;
    font-style: normal;
    color: ${cssVar('accentColorAlt')};

    @media ${from(Device.TabletLarge)} {
      font-size: 18px;
      line-height: 28px;
    }
  }

  .primary-colour {
    color: ${cssVar('accentColor')};
  }

  .secondary-colour {
    color: ${cssVar('accentColor2')};
  }

  span {
    display: inline;
  }
`;
const Container = styled(SectionWrapper) `
  ${setCssVar('accentColor', brand.hta.green)};
  ${setCssVar('accentColorAlt', brand.hta.greenDark)};
  ${setCssVar('accentColor2', brand.hta.purple)};
  ${setCssVar('bgColor', brand.greyLight)};
  ${setCssVar('fgColor', brand.black)};

  .theme-apl & {
    ${setCssVar('accentColor', brand.apl.green)};
    ${setCssVar('accentColorAlt', brand.apl.greenAlt)};
    ${setCssVar('accentColor2', brand.apl.greenLight)};
    ${setCssVar('fgColor', brand.black)};
  }

  .theme-hh & {
    ${setCssVar('accentColor', brand.hortHouse.green)};
    ${setCssVar('accentColorAlt', brand.white)};
    ${setCssVar('accentColor2', brand.hortHouse.orange)};
    ${setCssVar('fgColor', brand.white)};
  }

  .theme-rss & {
    ${setCssVar('accentColor', brand.rss.greenDark)};
    ${setCssVar('accentColor2', brand.rss.greenLight)};
    ${setCssVar('bgColor', brand.rss.grey)};
    ${setCssVar('fgColor', brand.white)};
  }

  @media ${from(Device.TabletLarge)} {
    opacity: 0;

    ${({ isActive }) => isActive &&
    css `
        ${fadeIn(isActive, 375, 0, animation.easeInOutCubic)};
      `}
  }

  ${({ hasBackground }) => hasBackground &&
    css `
      padding: 50px 0;
      background-color: ${cssVar('bgColor')};

      @media ${from(Device.TabletLarge)} {
        padding: 100px 0;
      }
    `}

  &[data-long-cta='true'] {
    ${LinkButtonStyles.Button} {
      width: 100%;
    }
  }

  ${LinkButtonStyles.Button} {
    margin: 30px 0;
  }
`;
const ContentBlock = styled.div `
  margin: 0;
  width: 100%;

  @media ${from(Device.TabletLarge)} {
    max-width: 800px;
  }

  ${({ align }) => align === 'center' &&
    css `
      margin: 0 auto;
    `}
`;
const Content = styled.div `
  ${contentStyles}

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    ${fonts.sourceSansPro.semiBold};
  }

  ${({ align }) => align === 'center' &&
    css `
      text-align: center;
      margin: 0 auto;
    `}

  ${({ align }) => align === 'left' &&
    css `
      text-align: left;

      h3 {
        @media ${from(Device.TabletLarge)} {
          width: 125%;
          max-width: 950px;
        }
      }
    `}

  ${({ leadParagraphWide, align }) => leadParagraphWide &&
    align === 'left' &&
    css `
      @media ${from(Device.TabletLarge)} {
        p:first-of-type {
          width: 125%;
          max-width: 950px;
        }
      }
    `}

    ${({ leadParagraphBold }) => leadParagraphBold &&
    css `
      p:first-of-type {
        ${ParagraphStyles.LargeStyles};
        ${fonts.sourceSansPro.semiBold};
      }
    `}
`;
export default {
    Container,
    Content,
    ContentBlock,
    contentStyles,
    linkStyles,
};
